/**
 * Shared staff session management for the web ops console.
 *
 * The website and the parkadda-b2b-app share the same backend auth
 * surface:
 *
 *   POST /api/events/staff/login/         → { token, staff }
 *   GET  /api/events/staff/session/       → { staff }    (needs Bearer)
 *   POST /api/events/staff/logout/        → invalidates token
 *
 * The token (a 64-char opaque string) is stored in localStorage so it
 * survives page reloads and tab switches.  We deliberately keep a
 * single token per browser — there is no multi-staff session concept
 * on the web side, matching the b2b app.
 *
 * Role flags are normalised from the backend ``staff`` payload into
 * the same capability booleans the Flutter ``StaffSession`` model
 * exposes, so component code reads the same way on both platforms.
 */

import { useCallback, useEffect, useState } from "react";
import { eventsApi } from "../../Events/api/eventsApi";

export var STAFF_TOKEN_KEY = "parkadda_staff_token";
var staffSubscribers = [];
var currentToken = typeof window !== "undefined" ? window.localStorage.getItem(STAFF_TOKEN_KEY) : null;
var currentStaff = null;

function notify() {
  for (var i = 0; i < staffSubscribers.length; i++) {
    try { staffSubscribers[i](currentToken, currentStaff); } catch (_) {}
  }
}

export function setStaffToken(token) {
  currentToken = token || null;
  if (typeof window !== "undefined") {
    if (token) window.localStorage.setItem(STAFF_TOKEN_KEY, token);
    else window.localStorage.removeItem(STAFF_TOKEN_KEY);
  }
  notify();
}

export function setStaffPayload(staff) {
  currentStaff = staff || null;
  notify();
}

export function getStaffToken() {
  return currentToken;
}

// ── Role → capability mapping ─────────────────────────────────────

// Mirrors parkadda-b2b-app/lib/Screens/EventsStaff/models/staff_session.dart
var ADMIN_ROLES   = ["SUPER_ADMIN", "EVENT_ADMIN"];
var VALET_ROLES   = ["SUPER_ADMIN", "EVENT_ADMIN", "ZONE_SUPERVISOR", "VALET_CAPTAIN", "VALET_OPERATOR"];
var CART_ROLES    = ["SUPER_ADMIN", "EVENT_ADMIN", "ZONE_SUPERVISOR", "CART_DISPATCHER", "CART_DRIVER"];
var CART_DRIVER   = ["CART_DRIVER"];
var VP_OPS_ROLES  = ["SUPER_ADMIN", "EVENT_ADMIN", "ZONE_SUPERVISOR", "POS_OPERATOR", "VENDOR_VIEWER"];
var VP_BILL_ROLES = ["SUPER_ADMIN", "EVENT_ADMIN", "POS_OPERATOR"];
var VP_FULFIL     = ["SUPER_ADMIN", "EVENT_ADMIN", "ZONE_SUPERVISOR", "POS_OPERATOR"];
var ACCRED_ROLES  = ["SUPER_ADMIN", "EVENT_ADMIN", "ACCREDITATION_LEAD"];

var BOOKING_ANALYTICS_ROLES = [
  "SUPER_ADMIN",
  "EVENT_ADMIN",
  "AUDITOR",
  "ZONE_SUPERVISOR",
  "BOOKING_VIEWER"
];

/** Web `/staff/bookings` only — no other ops modules (matches ``ROLE_BOOKING_VIEWER``). */
var BOOKING_VIEWER_ONLY_ROLE = "BOOKING_VIEWER";

export function describeRole(code) {
  if (!code) return "Staff";
  return String(code).replace(/_/g, " ").toLowerCase().replace(/\b\w/g, function (c) { return c.toUpperCase(); });
}

export function capabilities(staff) {
  if (!staff) {
    return {
      isSuper: false, isAdmin: false,
      canOperateValet: false, canOperateCart: false, isCartDriver: false,
      canOperateVendorPay: false, canBillVendorPay: false, canFulfilVendorPay: false,
      canManageAccreditation: false,
      canViewBookingAnalytics: false,
      canViewSiteFeedback: false,
      isBookingViewerOnly: false,
      canRecordCash: false,
      canManageAccounts: false,
      canViewSponsors: false,
      canEditSponsors: false,
      isCommitteeDesk: false,
      committeeCodes: []
    };
  }
  var role = staff.role_code || "";
  var committeeCodes = staff.committee_codes || (staff.scopes && staff.scopes.committee_codes) || [];
  var isBookingViewerOnly = role === BOOKING_VIEWER_ONLY_ROLE;
  var isSuper = !!staff.is_super || role === "SUPER_ADMIN";
  var isAdmin = isSuper || ADMIN_ROLES.indexOf(role) !== -1;
  var isCommitteeDesk = !isSuper && !isAdmin && committeeCodes.length > 0;
  var canViewBookingAnalytics;
  if (typeof staff.can_view_booking_analytics === "boolean") {
    canViewBookingAnalytics = staff.can_view_booking_analytics;
  } else {
    canViewBookingAnalytics = isSuper || BOOKING_ANALYTICS_ROLES.indexOf(role) !== -1;
  }
  var canViewSiteFeedback =
    typeof staff.can_view_site_feedback === "boolean"
      ? staff.can_view_site_feedback
      : isSuper || role === "EVENT_ADMIN";
  if (isBookingViewerOnly) {
    return {
      isSuper: false,
      isAdmin: false,
      canViewBookingAnalytics: canViewBookingAnalytics,
      canViewSiteFeedback: false,
      isBookingViewerOnly: true,
      canOperateValet: false,
      canOperateCart: false,
      isCartDriver: false,
      canOperateVendorPay: false,
      canBillVendorPay: false,
      canFulfilVendorPay: false,
      canManageAccreditation: false,
      canRecordCash: false,
      canManageAccounts: false,
      canViewSponsors: false,
      canEditSponsors: false,
      isCommitteeDesk: false,
      committeeCodes: []
    };
  }
  var canOperateVendorPay;
  if (typeof staff.can_operate_vendorpay === "boolean") {
    canOperateVendorPay = staff.can_operate_vendorpay;
  } else {
    canOperateVendorPay = !isCommitteeDesk && (isSuper || VP_OPS_ROLES.indexOf(role) !== -1);
  }
  return {
    isSuper: isSuper,
    isAdmin: isAdmin,
    canViewBookingAnalytics: canViewBookingAnalytics,
    canViewSiteFeedback: canViewSiteFeedback,
    isBookingViewerOnly: false,
    canOperateValet:      !isCommitteeDesk && (isSuper || VALET_ROLES.indexOf(role) !== -1 || !!staff.valet_role),
    canOperateCart:       !isCommitteeDesk && (isSuper || CART_ROLES.indexOf(role) !== -1),
    isCartDriver:         !isCommitteeDesk && CART_DRIVER.indexOf(role) !== -1,
    canOperateVendorPay:  canOperateVendorPay,
    canBillVendorPay:     canOperateVendorPay && (isSuper || VP_BILL_ROLES.indexOf(role) !== -1),
    canFulfilVendorPay:   canOperateVendorPay && (isSuper || VP_FULFIL.indexOf(role) !== -1),
    canManageAccreditation: !isCommitteeDesk && (isSuper || ACCRED_ROLES.indexOf(role) !== -1),
    canRecordCash: typeof staff.can_record_cash === "boolean"
      ? staff.can_record_cash
      : (isSuper || ["POS_OPERATOR", "EVENT_ADMIN", "ZONE_SUPERVISOR"].indexOf(role) !== -1),
    canManageAccounts: typeof staff.can_manage_accounts === "boolean"
      ? staff.can_manage_accounts
      : isAdmin,
    canViewSponsors: typeof staff.can_view_sponsors === "boolean"
      ? staff.can_view_sponsors
      : (isSuper || ["SUPER_ADMIN", "EVENT_ADMIN", "AUDITOR"].indexOf(role) !== -1),
    canEditSponsors: typeof staff.can_edit_sponsors === "boolean"
      ? staff.can_edit_sponsors
      : (isSuper || ["SUPER_ADMIN", "EVENT_ADMIN"].indexOf(role) !== -1),
    isCommitteeDesk: isCommitteeDesk,
    committeeCodes: committeeCodes
  };
}

// ── Hook ──────────────────────────────────────────────────────────

/**
 * useStaffSession — React hook giving a component access to the
 * current staff session.  Returns:
 *
 *   { token, staff, caps, status, error,
 *     login(mobile, password), logout(), refresh() }
 *
 *   status: "idle" | "loading" | "authenticated" | "unauthenticated"
 */
export function useStaffSession() {
  var _t = useState(currentToken);
  var token = _t[0], setTokenLocal = _t[1];
  var _s = useState(currentStaff);
  var staff = _s[0], setStaffLocal = _s[1];
  var _st = useState(currentToken ? "loading" : "unauthenticated");
  var status = _st[0], setStatus = _st[1];
  var _er = useState("");
  var error = _er[0], setError = _er[1];

  useEffect(function () {
    var cb = function (t, s) { setTokenLocal(t); setStaffLocal(s); };
    staffSubscribers.push(cb);
    return function () {
      staffSubscribers = staffSubscribers.filter(function (f) { return f !== cb; });
    };
  }, []);

  var refresh = useCallback(function () {
    if (!currentToken) { setStatus("unauthenticated"); return Promise.resolve(null); }
    setStatus("loading");
    return eventsApi.staffSession(currentToken).then(function (payload) {
      var s = (payload && payload.staff) || null;
      setStaffPayload(s);
      setStatus(s ? "authenticated" : "unauthenticated");
      return s;
    }).catch(function (e) {
      setError(e.message || "Session expired");
      setStaffToken(null);
      setStaffPayload(null);
      setStatus("unauthenticated");
      return null;
    });
  }, []);

  useEffect(function () {
    if (currentToken && !currentStaff) refresh();
    else if (currentStaff) setStatus("authenticated");
  }, [refresh]);

  var login = useCallback(function (mobile, password) {
    mobile = String(mobile || "").trim();
    setError("");
    setStatus("loading");
    return eventsApi.staffLogin({ mobile: mobile, password: password }).then(function (payload) {
      setStaffToken(payload && payload.token);
      setStaffPayload((payload && payload.staff) || null);
      setStatus("authenticated");
      return payload;
    }).catch(function (e) {
      setError(e.message || "Login failed");
      setStatus("unauthenticated");
      throw e;
    });
  }, []);

  var logout = useCallback(function () {
    var t = currentToken;
    setStaffToken(null);
    setStaffPayload(null);
    setStatus("unauthenticated");
    if (t) {
      eventsApi.staffLogout(t).catch(function () {});
    }
  }, []);

  return {
    token: token,
    staff: staff,
    caps: capabilities(staff),
    status: status,
    error: error,
    login: login,
    logout: logout,
    refresh: refresh
  };
}
